import React, { useEffect } from 'react'
import styled, { useTheme } from 'styled-components/native'

import { AccessibleTitle } from 'features/home/components/AccessibleTitle'
import { getCategoryBlockColor } from 'features/home/helpers/getCategoryColor'
import { CategoryBlock as CategoryBlockData } from 'features/home/types'
import { getCategoryLabelParts } from 'features/search/helpers/getCategoryLabelParts/getCategoryLabelParts'
import { analytics } from 'libs/analytics/provider'
import { ContentTypes } from 'libs/contentful/types'
import { useFeatureFlag } from 'libs/firebase/firestore/featureFlags/useFeatureFlag'
import { RemoteStoreFeatureFlags } from 'libs/firebase/firestore/types'
import { useMobileFontScaleToDisplay } from 'shared/accessibility/helpers/zoomHelpers'
import { CategoryButton } from 'shared/categoryButton/CategoryButton'
import { NewCategoryButton } from 'shared/categoryButton/NewCategoryButton'
import { categoryButtonIllustrationUrls } from 'shared/illustrations/categoryButtonIllustrations'
import { getSpacing } from 'ui/theme'
import { colorMapping } from 'ui/theme/colorMapping'

type CategoryListProps = {
  id: string
  title: string
  categoryBlockList: CategoryBlockData[]
  index: number
  homeEntryId: string
}

const BLOCK_HEIGHT = getSpacing(25)
const DESKTOP_MAX_WIDTH = getSpacing(37.33)
const DESKTOP_MIN_WIDTH = 'none'
const MOBILE_MIN_WIDTH = '40%'
const MOBILE_MIN_WIDTH_WHEN_FONT_ZOOMED = '100%'
const MOBILE_MAX_WIDTH = '49%'

export const CategoryListModule = ({
  id,
  title,
  categoryBlockList,
  index,
  homeEntryId,
}: CategoryListProps) => {
  const mobileMinWidth = useMobileFontScaleToDisplay({
    default: MOBILE_MIN_WIDTH,
    at200PercentZoom: MOBILE_MIN_WIDTH_WHEN_FONT_ZOOMED,
  })

  const { designSystem } = useTheme()

  const enableNewCategoryBlocks = useFeatureFlag(
    RemoteStoreFeatureFlags.WIP_NEW_CATEGORY_BLOCKS_HOME
  )

  useEffect(() => {
    void analytics.logModuleDisplayedOnHomepage({
      moduleId: id,
      moduleType: ContentTypes.CATEGORY_LIST,
      index,
      homeEntryId,
    })
  }, [id, homeEntryId, index])

  return (
    <Container>
      <AccessibleTitle title={title} />
      <StyledView>
        {categoryBlockList.map((item) => {
          const color = enableNewCategoryBlocks ? item.color : getCategoryBlockColor(item.color)
          const categoryColorMapping = colorMapping[color] ?? colorMapping.Information04

          const fillFromDesignSystem = enableNewCategoryBlocks
            ? designSystem.color.illustration[categoryColorMapping?.fill ?? 'default']
            : designSystem.color.background[categoryColorMapping?.fill ?? 'default']

          const borderFromDesignSystem =
            designSystem.color.border[categoryColorMapping?.border ?? 'default']
          return enableNewCategoryBlocks ? (
            <StyledNewCategoryButton
              key={item.id}
              label={item.title}
              height={BLOCK_HEIGHT}
              mobileMinWidth={mobileMinWidth}
              fillColor={fillFromDesignSystem || categoryColorMapping.fill}
              borderColor={borderFromDesignSystem || categoryColorMapping.border}
              onBeforeNavigate={() => {
                void analytics.logCategoryBlockClicked({
                  moduleId: item.id,
                  moduleListID: id,
                  entryId: homeEntryId,
                  toEntryId: item.homeEntryId,
                })
              }}
              navigateTo={{
                screen: 'ThematicHome',
                params: {
                  homeId: item.homeEntryId,
                  from: 'category_block',
                  moduleId: item.id,
                  moduleListId: id,
                },
              }}
              labelParts={item.titleParts ?? getCategoryLabelParts(item.title)}
              illustrationUrl={
                item.illustrationCategoryBlock
                  ? categoryButtonIllustrationUrls[item.illustrationCategoryBlock]
                  : undefined
              }
            />
          ) : (
            <StyledCategoryButton
              key={item.id}
              label={item.title}
              height={BLOCK_HEIGHT}
              mobileMinWidth={mobileMinWidth}
              fillColor={fillFromDesignSystem || categoryColorMapping.fill}
              borderColor={borderFromDesignSystem || categoryColorMapping.border}
              onBeforeNavigate={() => {
                void analytics.logCategoryBlockClicked({
                  moduleId: item.id,
                  moduleListID: id,
                  entryId: homeEntryId,
                  toEntryId: item.homeEntryId,
                })
              }}
              navigateTo={{
                screen: 'ThematicHome',
                params: {
                  homeId: item.homeEntryId,
                  from: 'category_block',
                  moduleId: item.id,
                  moduleListId: id,
                },
              }}
            />
          )
        })}
      </StyledView>
    </Container>
  )
}

const StyledView = styled.View(({ theme }) => {
  const MOBILE_GAPS_AND_PADDINGS = theme.designSystem.size.spacing.s
  const DESKTOP_GAPS_AND_PADDINGS = theme.designSystem.size.spacing.l
  return {
    flexDirection: 'row',
    width: '100%',
    paddingHorizontal: theme.contentPage.marginHorizontal,
    ...(theme.isMobileViewport
      ? {
          flexWrap: 'wrap',
          gap: MOBILE_GAPS_AND_PADDINGS,
          paddingVertical: MOBILE_GAPS_AND_PADDINGS,
        }
      : {
          gap: DESKTOP_GAPS_AND_PADDINGS,
          paddingVertical: DESKTOP_GAPS_AND_PADDINGS,
        }),
  }
})

const Container = styled.View(({ theme }) => ({
  marginBottom: theme.designSystem.size.spacing.xl,
}))

const StyledCategoryButton = styled(CategoryButton)<{ mobileMinWidth: string }>(
  ({ theme, mobileMinWidth }) => ({
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: 0,
    minWidth: theme.isMobileViewport ? mobileMinWidth : DESKTOP_MIN_WIDTH,
    maxWidth: theme.isMobileViewport ? MOBILE_MAX_WIDTH : DESKTOP_MAX_WIDTH,
  })
)

const StyledNewCategoryButton = styled(NewCategoryButton)<{ mobileMinWidth: string }>(
  ({ theme, mobileMinWidth }) => ({
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: 0,
    minWidth: theme.isMobileViewport ? mobileMinWidth : DESKTOP_MIN_WIDTH,
    maxWidth: theme.isMobileViewport ? MOBILE_MAX_WIDTH : DESKTOP_MAX_WIDTH,
  })
)
